import { useCallback, useContext } from "react";
import {
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import axios from "axios";
import { AccountContext } from "auth/accountProvider";

// Base URL of the consumer-data-processor API (same var the media queries use).
const API_BASE = process.env.REACT_APP_LPI_API_URL;

export type ReleaseNoteLang = "nl" | "en" | "de";
export type ReleaseNoteTag = "new" | "improved" | "fixed";

/** Per-language text, every language optional. */
export type Localized = Partial<Record<ReleaseNoteLang, string>>;

/** A release note as returned by GET /release-notes. */
export interface ReleaseNote {
  id: string;
  version: string;
  date: string | null;
  tag: ReleaseNoteTag | null;
  published: boolean;
  title: Localized;
  summary: Localized;
  details: Localized;
}

/** Body of a create; a patch is the same shape with every field optional. */
export interface ReleaseNoteInput {
  version: string;
  date?: string | null;
  tag?: ReleaseNoteTag | null;
  published?: boolean;
  title?: Localized;
  summary?: Localized;
  details?: Localized;
}

interface SessionTokens {
  jwtToken: string;
  sessionId: string;
}

// Mirrors the helper in mediaQueries — kept local rather than shared so neither
// module owns the other's session handling.
const useSessionTokens = (): (() => Promise<SessionTokens>) => {
  const { getSession } = useContext(AccountContext);
  return useCallback(async (): Promise<SessionTokens> => {
    const session = await getSession();
    if (!session) throw new Error("Session expired. Please log in again.");
    const jwtToken = session.getAccessToken?.()?.getJwtToken?.();
    const sessionId = session.getIdToken?.()?.payload?.sessionId;
    if (!jwtToken || !sessionId) {
      throw new Error("Session tokens missing. Please log in again.");
    }
    return { jwtToken, sessionId };
  }, [getSession]);
};

const authHeaders = (t: SessionTokens): Record<string, string> => ({
  Authorization: `Session ${t.jwtToken}`,
  "X-Session-Id": t.sessionId,
});

// --- Request functions (exported for testing; the hooks below wrap them) -----

/** Published notes only — what every authenticated user sees. */
export async function fetchPublishedReleaseNotes(
  auth: SessionTokens,
  signal?: AbortSignal
): Promise<ReleaseNote[]> {
  const res = await axios.get<{ items: ReleaseNote[] }>(
    `${API_BASE}/release-notes`,
    { headers: authHeaders(auth), signal }
  );
  return res.data.items ?? [];
}

/** Every note, drafts included — super_admin only. */
export async function fetchAllReleaseNotes(
  auth: SessionTokens,
  signal?: AbortSignal
): Promise<ReleaseNote[]> {
  const res = await axios.get<{ items: ReleaseNote[] }>(
    `${API_BASE}/release-notes/admin`,
    { headers: authHeaders(auth), signal }
  );
  return res.data.items ?? [];
}

export async function postReleaseNote(
  auth: SessionTokens,
  input: ReleaseNoteInput
): Promise<ReleaseNote> {
  const res = await axios.post<ReleaseNote>(
    `${API_BASE}/release-notes/admin`,
    input,
    { headers: authHeaders(auth) }
  );
  return res.data;
}

export async function patchReleaseNote(
  auth: SessionTokens,
  id: string,
  input: Partial<ReleaseNoteInput>
): Promise<ReleaseNote> {
  const res = await axios.patch<ReleaseNote>(
    `${API_BASE}/release-notes/admin/${id}`,
    input,
    { headers: authHeaders(auth) }
  );
  return res.data;
}

export async function deleteReleaseNote(
  auth: SessionTokens,
  id: string
): Promise<void> {
  await axios.delete(`${API_BASE}/release-notes/admin/${id}`, {
    headers: authHeaders(auth),
  });
}

// --- Hooks -------------------------------------------------------------------

/** Published release notes, newest version first (the API orders them). */
export const useReleaseNotes = (): UseQueryResult<ReleaseNote[]> => {
  const getAuth = useSessionTokens();
  return useQuery({
    queryKey: ["releaseNotes", "published"] as const,
    queryFn: async ({ signal }): Promise<ReleaseNote[]> =>
      fetchPublishedReleaseNotes(await getAuth(), signal),
  });
};

/** All release notes including drafts — used by the admin page. */
export const useAllReleaseNotes = (): UseQueryResult<ReleaseNote[]> => {
  const getAuth = useSessionTokens();
  return useQuery({
    queryKey: ["releaseNotes", "all"] as const,
    queryFn: async ({ signal }): Promise<ReleaseNote[]> =>
      fetchAllReleaseNotes(await getAuth(), signal),
  });
};

/**
 * Authoring operations. Each invalidates every release-notes query on success,
 * so publishing a draft also refreshes the public list behind the Overview card.
 */
export const useReleaseNoteAdmin = () => {
  const getAuth = useSessionTokens();
  const qc = useQueryClient();

  const invalidate = useCallback(
    () => qc.invalidateQueries({ queryKey: ["releaseNotes"] }),
    [qc]
  );

  const create = useCallback(
    async (input: ReleaseNoteInput): Promise<ReleaseNote> => {
      const note = await postReleaseNote(await getAuth(), input);
      await invalidate();
      return note;
    },
    [getAuth, invalidate]
  );

  const update = useCallback(
    async (id: string, input: Partial<ReleaseNoteInput>): Promise<ReleaseNote> => {
      const note = await patchReleaseNote(await getAuth(), id, input);
      await invalidate();
      return note;
    },
    [getAuth, invalidate]
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await deleteReleaseNote(await getAuth(), id);
      await invalidate();
    },
    [getAuth, invalidate]
  );

  return { create, update, remove };
};
